import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// Services
import { HttpService } from '@/Services/http.service';
import { LoggerService } from '@/Services/log-level.service';

// PrimeNg
import { MessageService } from 'primeng/api';

// Interfaces
import { IParams } from '@/interface/general/params.interface';
import {
  IResponseApi,
  IResponseApiSS,
  IResponseLogin,
} from '@/interface/general/IResponseApi';
import {
  IEmailFormData,
  IMailData,
  IResponseListFilesParams,
  IUpdateAttachementSelect,
} from '../interface/send-mail.interface';

import {
  IUserPermissions,
  TipoPermiso,
} from '@/shared/interfaces/Permissions.interface';
import { TypeResponse } from '@/common/enums/general/TypeResponse';
import { PermissionsService } from '@/shared/service/Permissions.service';

// Tipado
export type KeyValueArrayGeneric<T = string | number> = IParams<T>[];

@Injectable({
  providedIn: 'root',
})
export class SendMailService<T> {
  //URLS

  //aqui van las url
  private urlGetById = 'Mail/GetById?Id=';
  private urlGetAll = 'Mail/GetAll';
  private urlCargueInicial = 'Mail/GetInitialData';
  private urlPostCreate = 'Mail/Create';
  private urlPutUpdate = 'Mail/Update';
  private urlDelete = 'Mail/Delete?idEntity=';

  private urlGetListAttach = 'mail/GetListAttach';
  private urlGetUploadAttach = 'mail/UploadMailAttach';
  private urlEnvioCorreo = 'mail/aqui';
  private urlUpdateAttachementSelect = 'mail/UpdateAttachementSelect';

  private http = inject(HttpService);
  private logger = inject(LoggerService);
  private messageService = inject(MessageService);
  permissonService = inject(PermissionsService);

  /**
   * Metodo necesario para guardar las nuevas paginas que se agregaran al pdf
   * @param pdfObject Objeto con los datos necesarios para guardar el pdf
   * @returns
   */
  private save(pdfObject: object, patchEndPoint: string) {
    return this.http.Post(pdfObject, patchEndPoint);
  }

  sendToSave(data: object, patchEndPoint: string) {
    this.save(data, patchEndPoint).subscribe({
      next: (success: IResponseLogin) => {
        this.logger.log(success);
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Proceso ejecutado exitosamente!!!',
          life: 2000,
        });
      },
      error: (err: HttpErrorResponse) => {
        this.logger.log(err);
        alert(err.message);
        // this.messageService.add({
        //   severity: 'error',
        //   summary: 'Error',
        //   detail: 'Error en el proceso. Intente nuevamente.',
        //   life: 3000,
        // });
      },
    });
  }

  /**
   * Metodo que consulta el listado de paginas del pdf
   * @paramsObject Un array de objetos { key: string; value: string }[]
   * @returns resultado del backend
   */
  getMailRedenderiado(datos: KeyValueArrayGeneric<T>, patchEndPoint: string) {
    const params = this.dynamicParams(datos);

    return this.http.GetParams(patchEndPoint, params);
  }

  /**
   * Consultamos a End Point enviadole params
   * @param datos
   * @param patchApi
   * @returns Observable<IResponseReporteador> 
   * @ejemplo Uso con tipo string
      const stringArray: KeyValueArrayGeneric<string> = [
        { key: "name", value: "Alice" }, // Correcto
        { key: "email", value: "alice@example.com" }, // Correcto
        // { key: "age", value: 30 } // Error: number no es asignable a string
      ];

      // Uso con tipo number (usando el default any)
      const anyArray: KeyValueArrayGeneric = [
        { key: "age", value: 30 },
        { key: "active", value: true }
      ];
  */
  getWitchParams(
    parametros: KeyValueArrayGeneric<T>,
    patchApi: string
  ): Observable<IResponseListFilesParams> {
    const params = this.dynamicParams(parametros);
    return this.http.GetParams<IResponseListFilesParams>(patchApi, params);
  }

  /**
   * Metodo que consulta el listado de archivos
   * @paramsObject Un array de objetos { key: string; value: string }[]
   * @returns resultado del backend
   */
  uploadFile(
    file: File,
    patchEndPoint: string
  ): Observable<IResponseListFilesParams> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.PostFormData(formData, patchEndPoint);
  }

  uploadFileBase64(data: {
    FilesBase64: {
      Base64Content: string;
      FileExtension: string;
    }[];
    Id: number;
    CodGlobal: string;
    CodTiposDocumentales: number;
  }): Observable<IResponseListFilesParams> {
    return this.http.Post(data, this.urlGetUploadAttach);
  }


  dynamicParams(filtros: KeyValueArrayGeneric<T>): HttpParams {
    let params = new HttpParams();

    // Iteramos sobre cada elemento del array de filtros
    filtros.forEach(({ key, value }) => {
      // Solo agregamos los parámetros que tienen valor y no son null/undefined
      if (value !== null && value !== undefined && value !== '') {
        // Convertimos el valor a string (necesario para HttpParams)
        const stringValue = String(value);
        params = params.append(key, stringValue);
      }
    });

    return params;
  }

  //#region  CARGA INICIAL
  /**
   * Carga datos iniciales si son necesarios para el formulario
   */
  public loadInitialDataIfNeeded(): void {
    this.getInitialParameters$().subscribe({
      next: (response) => {
        if (response?.status === TypeResponse.OK) {
          const programasPermisosList: IUserPermissions[] =
            response.data.ProgramasPermisosList;

          this.permissonService.setActiveProgramAndButtons('mail', ['grabar']);

          this.permissonService.setPermissionsForProgram({
            CodPrograma: 'mail',
            Grabar: true, // Tiene permiso para guardar
            Editar: false,
            Borrar: false,
            Leer: true,
            Consultar: false,
            Ejecutar: false,
            SendMail: false,
          });

          programasPermisosList[0].SendMail = true;
          this.permissonService.setPermissionsForProgram(
            programasPermisosList[0]
          );
        }
      },
      error: (err) => {
        console.error('Error en carga inicial:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar datos iniciales',
          life: 3000,
        });
      },
    });
  }
  //#endregion

  //#region CRUD DE LISTA/CUERPO
  // Crear
  postCreateMail(data: IEmailFormData): Observable<IResponseApiSS> {
    return this.http.Post(data, this.urlPostCreate);
  }
  //Editar
  PutUpdateMail(data: IEmailFormData): Observable<IResponseApiSS> {
    return this.http.Put(data, this.urlPutUpdate);
  }
  // Eliminar
  deleteMail(Id: number) {
    return this.http.Delete(this.urlDelete + `${Id}`);
  }
  //buscar id
  getMailById(id: number): Observable<IMailData> {
    return this.http.Get<IMailData>(`${this.urlGetById}/${id}`);
  }
  //carga inicial
  getInitialParameters$(): Observable<any> {
    return this.http.Get(this.urlCargueInicial);
  }
  //carga inicial
  postSendMail(data: any): Observable<IResponseApiSS> {
    return this.http.Post(data, this.urlEnvioCorreo);
  }

  // getFilasTablas(params: any): Observable<IDataPagination<any[]>> {
  //   return this.http.Get<IDataPagination<any[]>>(this.urlGetFileTabla, params);
  // }
  //#endregion

  putAttachementSelect(
    data: IUpdateAttachementSelect
  ): Observable<IResponseApi> {
    return this.http.Put(data, this.urlUpdateAttachementSelect);
  }
}
